import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Plus } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES } from "../constants/site";
import Reveal from "../components/Reveal";
import Field from "../components/Field";
import { useSeo } from "../hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function useDriverForm(t, lang) {
  const [values, setValues] = useState({
    full_name: "", email: "", phone: "", city: "",
    driving_license_number: "", experience_years: "", languages: "", notes: ""
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setValues({ ...values, [k]: e.target.value });

  const validate = () => {
    const e = {};
    if (!values.full_name || values.full_name.trim().length < 2) e.full_name = t.errors.required;
    if (!values.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) e.email = t.errors.email;
    if (!values.phone || values.phone.replace(/\D/g, "").length < 8) e.phone = t.errors.phone;
    if (!values.city || values.city.trim().length < 2) e.city = t.errors.required;
    if (!values.driving_license_number || values.driving_license_number.trim().length < 4) e.driving_license_number = t.errors.license;
    const y = Number(values.experience_years);
    if (values.experience_years === "" || Number.isNaN(y) || y < 0 || y > 60) e.experience_years = t.errors.experience;
    if (!values.languages || values.languages.trim().length < 2) e.languages = t.errors.required;
    return e;
  };

  const submit = async (onSuccess) => {
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/drivers`, { ...values, experience_years: Number(values.experience_years), lang });
      toast.success(t.driver.form.successTitle);
      onSuccess(res.data.id);
    } catch (err) {
      toast.error(t.errors.generic);
    } finally {
      setBusy(false);
    }
  };

  return { values, errors, busy, set, validate, submit };
}

function Faq({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid={`faq-item-${i}`}>
      <button
        type="button"
        data-testid={`faq-toggle-${i}`}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 py-5 text-left"
      >
        <span className="font-display text-base lg:text-lg font-semibold text-white">{q}</span>
        <Plus size={18} className={`shrink-0 text-gold transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
        <p className="overflow-hidden text-sm text-slate-400 leading-relaxed pb-5 pr-8">{a}</p>
      </div>
    </div>
  );
}

export default function DriverPage() {
  const { t, lang } = useLanguage();
  useSeo(
    "Become a Tesla Chauffeur in Antwerp | MIRAL GROUP",
    "Join MIRAL GROUP as a chauffeur: Tesla provided, premium clientele, flexible shifts. Apply online — our team replies within 48 hours."
  );
  const [submitted, setSubmitted] = useState(null);
  const f = useDriverForm(t, lang);

  const onSuccess = (id) => {
    setSubmitted(id);
    f.setValues({
      full_name: "", email: "", phone: "", city: "",
      driving_license_number: "", experience_years: "", languages: "", notes: ""
    });
  };

  return (
    <main className="pt-[72px]">
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <Reveal>
            <p className="eyebrow">{t.driver.eyebrow}</p>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight max-w-[16ch]">
              {t.driver.title}
            </h1>
            <p className="mt-6 max-w-[60ch] text-lg text-slate-300">{t.driver.sub}</p>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-white/10 bg-[#090a0e] py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-5 lg:px-10 grid gap-14 lg:grid-cols-2">
          <div>
            <Reveal>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight">{t.driver.benefitsTitle}</h2>
            </Reveal>
            <div className="mt-8 space-y-0">
              {t.driver.benefits.map((b, i) => (
                <Reveal key={b} delay={i * 0.07}>
                  <div data-testid={`driver-benefit-${i}`} className="flex items-start gap-4 border-t border-white/10 py-5 last:border-b">
                    <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-gold" />
                    <p className="text-slate-300">{b}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.15}>
              <div className="mt-10 relative rounded-2xl overflow-hidden border border-white/10 hidden lg:block">
                <img src={IMAGES.chauffeur} alt="MIRAL chauffeur" className="h-64 w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#070709] via-transparent to-transparent" />
              </div>
            </Reveal>
          </div>

          <div>
            <Reveal>
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight">{t.driver.reqTitle}</h2>
              <ul className="mt-6 space-y-3">
                {t.driver.reqs.map((r, i) => (
                  <li key={r} data-testid={`driver-requirement-${i}`} className="flex items-start gap-3 text-sm text-slate-300">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" /> {r}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.1}>
              <div data-testid="driver-application-form" className="mt-12 rounded-2xl border border-white/10 bg-[#0F1015] p-6 lg:p-9">
                <h2 className="font-display text-xl lg:text-2xl font-bold text-white">{t.driver.formTitle}</h2>
                <p className="mt-2 text-sm text-slate-400">{t.driver.formSub}</p>

                {submitted ? (
                  <div data-testid="driver-form-success" className="mt-8 rounded-xl border border-gold/30 bg-gold/5 p-6 text-center">
                    <CheckCircle2 size={36} className="mx-auto text-gold" />
                    <h3 className="mt-4 font-display text-lg font-bold text-white">{t.driver.form.successTitle}</h3>
                    <p className="mt-2 text-sm text-slate-300">{t.driver.form.successBody}</p>
                    <p className="mt-4 font-mono2 text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      {t.driver.form.ref}: <span className="text-gold-soft">{submitted}</span>
                    </p>
                    <button
                      type="button"
                      data-testid="driver-form-again-button"
                      onClick={() => setSubmitted(null)}
                      className="btn-outline mt-6 !py-2.5 !px-6 text-[11px] font-mono2 uppercase tracking-[0.18em]"
                    >
                      {t.driver.form.again}
                    </button>
                  </div>
                ) : (
                  <form
                    className="mt-7 grid gap-5 sm:grid-cols-2"
                    data-testid="driver-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      f.submit(onSuccess);
                    }}
                    noValidate
                  >
                    <Field id="driver-full-name" label={t.driver.form.fullName} error={f.errors.full_name} required>
                      <input
                        id="driver-full-name"
                        data-testid="driver-full-name-input"
                        className={`field-input ${f.errors.full_name ? "has-error" : ""}`}
                        value={f.values.full_name}
                        onChange={f.set("full_name")}
                        autoComplete="name"
                      />
                    </Field>
                    <Field id="driver-email" label={t.driver.form.email} error={f.errors.email} required>
                      <input
                        id="driver-email"
                        data-testid="driver-email-input"
                        type="email"
                        className={`field-input ${f.errors.email ? "has-error" : ""}`}
                        value={f.values.email}
                        onChange={f.set("email")}
                        autoComplete="email"
                      />
                    </Field>
                    <Field id="driver-phone" label={t.driver.form.phone} error={f.errors.phone} required>
                      <input
                        id="driver-phone"
                        data-testid="driver-phone-input"
                        type="tel"
                        className={`field-input ${f.errors.phone ? "has-error" : ""}`}
                        value={f.values.phone}
                        onChange={f.set("phone")}
                        autoComplete="tel"
                      />
                    </Field>
                    <Field id="driver-city" label={t.driver.form.city} error={f.errors.city} required>
                      <input
                        id="driver-city"
                        data-testid="driver-city-input"
                        className={`field-input ${f.errors.city ? "has-error" : ""}`}
                        value={f.values.city}
                        onChange={f.set("city")}
                      />
                    </Field>
                    <Field id="driver-license" label={t.driver.form.license} error={f.errors.driving_license_number} required>
                      <input
                        id="driver-license"
                        data-testid="driver-license-input"
                        className={`field-input ${f.errors.driving_license_number ? "has-error" : ""}`}
                        value={f.values.driving_license_number}
                        onChange={f.set("driving_license_number")}
                      />
                    </Field>
                    <Field id="driver-experience" label={t.driver.form.experience} error={f.errors.experience_years} required>
                      <input
                        id="driver-experience"
                        data-testid="driver-experience-input"
                        type="number"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        enterKeyHint="next"
                        min="0"
                        max="60"
                        className={`field-input ${f.errors.experience_years ? "has-error" : ""}`}
                        value={f.values.experience_years}
                        onChange={f.set("experience_years")}
                      />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field id="driver-languages" label={t.driver.form.languages} error={f.errors.languages} required>
                        <input
                          id="driver-languages"
                          data-testid="driver-languages-input"
                          className={`field-input ${f.errors.languages ? "has-error" : ""}`}
                          value={f.values.languages}
                          onChange={f.set("languages")}
                          placeholder={t.driver.form.languagesPh}
                        />
                      </Field>
                    </div>
                    <div className="sm:col-span-2">
                      <Field id="driver-notes" label={t.driver.form.notes}>
                        <textarea
                          id="driver-notes"
                          data-testid="driver-notes-input"
                          rows={3}
                          className="field-input resize-none"
                          value={f.values.notes}
                          onChange={f.set("notes")}
                          placeholder={t.driver.form.notesPh}
                        />
                      </Field>
                    </div>
                    <button
                      type="submit"
                      data-testid="driver-form-submit-button"
                      disabled={f.busy}
                      className="btn-gold sm:col-span-2 w-full text-xs font-mono2 uppercase tracking-[0.18em] disabled:opacity-60"
                    >
                      {f.busy ? "…" : t.driver.form.submit}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-3xl px-5 lg:px-10">
          <Reveal>
            <h2 className="text-center font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">{t.driver.faqTitle}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 border-t border-white/10">
              {t.driver.faq.map((item, i) => (
                <Faq key={item.q} q={item.q} a={item.a} i={i} />
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
